import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCatalogKey,
  ShopifyCatalogToMarketKey,
  ShopifyCompanyLocationKey,
  ShopifyCompanyLocationToMarketKey,
  ShopifyCustomerKey,
  ShopifyCustomerToMarketKey,
  ShopifyMarketCatalogKey,
  ShopifyMarketCatalogToCatalogKey,
  ShopifyMarketCatalogToMarketKey,
  ShopifyMarketKey,
  ShopifyMarketPrimaryWebPresenceToMarketKey,
  ShopifyMarketRegionKey,
  ShopifyMarketRegionToMarketKey,
  ShopifyMarketToCatalogKey,
  ShopifyMarketToCompanyLocationsKey,
  ShopifyMarketToCustomersKey,
  ShopifyMarketToShopKey,
  ShopifyMarketWebPresenceKey,
  ShopifyMarketWebPresenceToMarketKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyMarketKey,
  defaultFilterableApiIdentifiers: ["primary"],
  "2022-04": {
    name: "Shopify Market",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
          },
        },
      },
      {
        name: "Currency Settings",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "currencySettings",
            graphQLSelection: `
            baseCurrency {
              currencyCode
              currencyName
              enabled
              rateUpdatedAt
            }
            localCurrencies`,
          },
        },
      },
      {
        name: "Enabled",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "enabled",
            graphQLFieldKey: "enabled",
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
          },
        },
      },
      {
        name: "Primary",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "primary",
          },
        },
      },
      {
        name: "Regions",
        fieldType: FieldType.HasMany,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyMarketRegionKey,
          inverseFieldKey: ShopifyMarketRegionToMarketKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "regions",
          },
        },
      },
      {
        name: "Market Web Presence",
        fieldType: FieldType.HasOne,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "webPresence",
            graphQLSelection: `
            alternateLocales
            defaultLocale
            id
            rootUrls {
              locale
              url
            }
            subfolderSuffix
            domain {
              id
            }`,
            shopifyFieldKey: "webPresence",
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyMarketWebPresenceKey,
          inverseFieldKey: ShopifyMarketWebPresenceToMarketKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyMarketToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "markets",
      filters: {},
      sortableBy: {
        createdAt: { sortKey: "CREATED_AT", reverse: true },
        updatedAt: { sortKey: "UPDATED_AT", reverse: true },
      },
    },
  },
  "2023-04": {
    hasMetafields: true,
    fields: [
      {
        name: "Company Locations",
        fieldType: FieldType.HasMany,
        key: ShopifyMarketToCompanyLocationsKey,
        configuration: {
          relatedModelKey: ShopifyCompanyLocationKey,
          inverseFieldKey: ShopifyCompanyLocationToMarketKey,
        },
      },
    ],
  },
  "2023-10": {
    fields: [
      {
        key: ShopifyMarketPrimaryWebPresenceToMarketKey,
        name: "Market Web Presence",
        fieldType: FieldType.BelongsTo,
        configuration: {
          relatedModelKey: ShopifyMarketWebPresenceKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "webPresence",
            shopifyFieldKey: "webPresence",
            skipGraphQLSelection: true,
          },
        },
      },
      {
        name: "Market Web Presences",
        fieldType: FieldType.HasMany,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "webPresences",
            graphQLSelection: `
            alternateLocales
            defaultLocale
            id
            rootUrls {
              locale
              url
            }
            subfolderSuffix
            domain {
              id
            }`,
            shopifyFieldKey: "webPresences",
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyMarketWebPresenceKey,
          inverseFieldKey: ShopifyMarketWebPresenceToMarketKey,
        },
      },
    ],
  },
  "2024-04": {
    fields: [
      {
        name: "Market Catalogs",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyMarketToCatalogKey,
        configuration: {
          joinModelKey: ShopifyMarketCatalogKey,
          relatedModelKey: ShopifyCatalogKey,
          inverseJoinModelFieldKey: ShopifyMarketCatalogToMarketKey,
          inverseRelatedModelFieldKey: ShopifyMarketCatalogToCatalogKey,
          inverseFieldKey: ShopifyCatalogToMarketKey,
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "Customers",
        fieldType: FieldType.HasMany,
        key: ShopifyMarketToCustomersKey,
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
          inverseFieldKey: ShopifyCustomerToMarketKey,
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
            availableInWebhook: ["markets/create", "markets/update"],
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
            availableInWebhook: ["markets/create", "markets/update"],
          },
        },
      },
    ],
  },
};

export const ShopifyMarket = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
